import { FavouriteDefinition, FavouriteCondition, AdvancedSearchTypes } from 'app/types-shared';
import {
  Filter, FilterMenuOptions, FilterMenuOptionsTypes,
  ConnectionsOperatorTypes, FilterDomains, FilterDomainNode
} from 'app/components/filters/types';
import { FilterTypes } from 'app/components/filters/types';
import { mapConnections } from 'app/area/filters/favourite-definition/map-connections';
import { mapInitialConditions } from 'app/area/filters/favourite-definition/map-initial-conditions';
import { mapOptionType } from './map-option-type';
import { groupConditions } from './group-conditions';

let filtersIdCounter = 1;

const initialFilter = (filterDomain: number) => {
  return {
    id: filterDomain + '_' + (filtersIdCounter++),
    initialValue: undefined,
    conditions: [],
    menuOptions: [],
    optionType: undefined,
    subOptionTypes: [],
    isOpen: false
  };
};

/**
 * Creates filters options menu based on data from favourite definition condition
 *
 * @param condition Condition from favourite definition
 */
export const mapMenuOptions = (condition: FavouriteCondition) => [
  {
    id: 'MainLevel',
    option: condition.conditionOperator2 && condition.conditionOperator2.v === ConnectionsOperatorTypes.exclude
      ? FilterMenuOptions.Exclude
      : FilterMenuOptions.Include,
    type: FilterMenuOptionsTypes.MainLevel
  },
  {
    id: 'SubLevel',
    option: condition.exactLevel
      ? FilterMenuOptions.ExcludeSubLevels
      : FilterMenuOptions.IncludeSubLevels,
    type: FilterMenuOptionsTypes.SubLevel
  }
];

/**
 * Extends the filter with unique id and menuOptions holder
 *
 * @param filter
 */
export function mapToFilter(filter: Filter): Filter {
  return {
    ...filter,
    ...initialFilter(filter.domain)
  };
}

export const advancedField = (advancedSearchTypes: AdvancedSearchTypes[], filterType: FilterTypes) =>
  advancedSearchTypes && advancedSearchTypes.find(item => item.id.v === filterType)
  || undefined;

/**
 * Creates filters list from favourite definition and filterDomains list
 *
 * @param definition
 * @param filterDomains
 * @param predefinedLabel
 */
export const createFiltersDefinition = (
  definition: Partial<FavouriteDefinition>,
  filterDomains: FilterDomainNode[],
  advancedSearchTypes: AdvancedSearchTypes[],
  predefinedLabel: string): Filter[] => {

  // Creates predefine filter
  const predefinedFilter: Filter = {
    ...initialFilter(FilterDomains.PredefFilters),
    domain: FilterDomains.PredefFilters,
    label: predefinedLabel,
    advancedSearchType: FilterTypes.PredefFilters,
    advancedField: advancedField(advancedSearchTypes, FilterTypes.PredefFilters),
    initialValue: !!definition && mapConnections(definition.connections)
  };

  // Creates list of empty filters based on filterDomains
  const emptyFilters: Filter[] = !!filterDomains
    ? filterDomains.map(filterDomain => ({
      ...filterDomain,
      ...initialFilter(filterDomain.domain),
      advancedField: advancedField(advancedSearchTypes, filterDomain.advancedSearchType)
    }))
    : [];

  // Creates list of filters based on favourite definition
  const newFilters: Filter[] = !!definition ?
    groupConditions(definition.conditions)
      .map(conditions => {
        const condition = conditions[0];
        return {
          ...initialFilter(condition.domain.v),
          domain: condition.domain.v,
          label: !!condition.domain.ald ? condition.domain.ald : condition.domain.d,
          advancedSearchType: condition.advancedSearchType,
          initialValue: mapInitialConditions(conditions),
          menuOptions: mapMenuOptions(condition),
          optionType: mapOptionType(condition),
          searchRequired: condition.searchRequired,
          advancedField: advancedField(advancedSearchTypes, condition.advancedSearchType),
          hasDisabledColumn: condition.hasDisabledColumn,
          conditions
        } as Filter;
      })
    : [];

  // Replaces empty filters with the one created based on favourite for the same domain
  const condidtionFilters = [...emptyFilters, ...newFilters].reduce((memo, filter) => {
    const placeholderIndex = memo.findIndex(f => f.domain === filter.domain && !f.initialValue);

    if (placeholderIndex !== -1) {
      memo.splice(placeholderIndex, 1, filter);
      return memo;
    }
    return [...memo, filter];
  }, [] as Filter[]);

  return [
    predefinedFilter,
    ...condidtionFilters
  ];
};
